import dynamic from 'next/dynamic';

import { CatalogServiceProductData, getProductsFromCatalogService } from 'src/services/catalog';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { transformProductModelToProductForTracking } from 'src/tracking/common/productTransformer';
import { trackImpression, trackLinkClick } from 'src/tracking/components/partnerBlock';
import { ContentBlockRenderer } from 'src/types/Block';
import { PlacementContext } from 'src/types/PlacementContext';
import { AsyncTransformer } from 'src/types/Transformer';
import {
  PartnerBlockProps,
  PartnerProfileIndexes,
} from '../../../components/blocks/PartnerBlockBlock';
import { contentPartnerToModel } from './contentToModel';
import { productModelToPartnerProduct } from './modelToUI';
import { ContentPartner, ContentPartnerBlockSection, PartnerBlockSerializedData } from './types';
import { getProductsValidationErrors } from './validation';

const hideNonPurchasableProducts = (products: CatalogServiceProductData[]) =>
  products.filter((product) => product.purchasable);

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<PartnerBlockSerializedData, PartnerBlockProps> = {
  Component: dynamic<PartnerBlockProps>(() =>
    import('src/components/blocks/PartnerBlockBlock').then((mod) => mod.PartnerBlockBlock),
  ),
  getProps: ({ ...serializedData }) => {
    const {
      currency,
      trackingPosition,
      trackingTitle,
      partners,
      pageType,
      ...restOfSerializedData
    } = serializedData;

    const dataForTracking: PlacementContext = {
      placementContentType: 'Partner block',
      trackingPosition,
      trackingTitle,
    };

    const partnersWithRenderedProducts = partners.map((partner) => ({
      ...partner,
      products: hideNonPurchasableProducts(partner.products),
    }));

    const partnersWithTransformedProducts = partnersWithRenderedProducts.map((partner) => ({
      ...partner,
      products: partner.products.map((product) => productModelToPartnerProduct(product, currency)),
    }));

    return {
      ...restOfSerializedData,
      partners: partnersWithTransformedProducts,
      onProductVisible: ({ partnerIndex }: { partnerIndex: number }) => {
        if (partnersWithRenderedProducts[partnerIndex].products.length > 0) {
          trackProductImpression({
            ...dataForTracking,
            pageType,
            products: partnersWithRenderedProducts[partnerIndex].products.map(
              transformProductModelToProductForTracking,
            ),
          });
        }
      },
      onProductClick: (
        e: React.MouseEvent<HTMLAnchorElement>,
        { partnerIndex, productIndex }: PartnerProfileIndexes,
      ) => {
        if (partnersWithRenderedProducts[partnerIndex].products.length > 0) {
          trackProductClick(e, {
            ...dataForTracking,
            pageType,
            numberOfItems: partnersWithRenderedProducts[partnerIndex].products.length,
            product: transformProductModelToProductForTracking(
              partnersWithRenderedProducts[partnerIndex].products[productIndex],
            ),
            productIndex,
          });
        }
      },
      onVisible: () =>
        trackImpression({
          ...dataForTracking,
          numberOfPartners: partnersWithRenderedProducts.length,
        }),
      onPartnerClick: (
        e: React.MouseEvent<HTMLAnchorElement>,
        { partnerIndex }: { partnerIndex: number },
      ) => {
        trackLinkClick(e, {
          ...dataForTracking,
          numberOfPartners: partnersWithRenderedProducts.length,
          partnerIndex,
          // get the partner name from the first product
          // assume both products are from the same partner
          partnerName: partnersWithRenderedProducts[partnerIndex].products[0].partner.name,
          partnerUrl: partnersWithRenderedProducts[partnerIndex].url,
        });
      },
    };
  },
};

const getAllProductCodesFromPartners = (partners: ContentPartner[]) =>
  partners
    .reduce((prev, curr) => prev.concat(curr.products as never), [])
    .map(({ product_code }) => Number(product_code));

export const transformer: AsyncTransformer<
  ContentPartnerBlockSection,
  PartnerBlockSerializedData,
  PartnerBlockProps
> = async ({ _metadata, heading, copy, partners, tracking_title }, { currency, pageType }) => {
  const productCodes = getAllProductCodesFromPartners(partners);
  const catalogServiceProducts = await getProductsFromCatalogService(productCodes);
  const renderableProducts = (catalogServiceProducts || [])?.filter(
    (product) => product.purchasable,
  );
  const errors = getProductsValidationErrors(
    productCodes,
    catalogServiceProducts,
    renderableProducts,
  );

  return {
    uid: _metadata.uid,
    renderer,
    serializedData: {
      heading,
      copy,
      partners: partners.map((partner) => contentPartnerToModel(partner, renderableProducts)),
      trackingTitle: tracking_title,
      currency,
      pageType,
    },
    errors,
  };
};
